// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const getUploadURL = body => {
  return post('/api/get-upload-url', body);
};

export const getDownloadURL = body => {
  return post('/api/get-download-url', body);
};

export const enable2fa = body => {
  return post('/api/auth/enable2fa', body);
};

export const verify2fa = body => {
  return post('/api/auth/verify2fa', body);
};

export const signin = body => {
  return post('/api/auth/signin', body);
};

export const signin2fa = body => {
  return post('/api/auth/signin2fa', body);
};

export const createTransaction = body => {
  return post('/api/midtrans/create-transaction', body);
};

export const confirmPayment = body => {
  return post('/api/midtrans/confirm-payment', body);
};

export const refundOrCancelTransaction = body => {
  return post('/api/midtrans/refund-or-cancel-transaction', body);
};

export const getSupportedBanks = body => {
  return post('/api/midtrans/banks', body);
};

export const validateBankAccount = body => {
  return post('/api/midtrans/validate-bank-account', body);
};

export const savePayout = body => {
  return post('/api/midtrans/payout-details', body);
};

export const refundDeposit = body => {
  return post('/api/midtrans/refund-deposit', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// /seo-settings admin page (src/containers/SeoSettingsPage). Each endpoint
// independently re-checks admin status server-side — see
// server/api-util/seoAdmin.js — so a non-admin logged-in user calling these
// directly gets a 403, not real data.
//
// See `server/api/seo-admin/list.js` to see the response shape:
// { categories, overrides, cat4Terms, cat4Entries }.
export const listSeoAdminCategories = () => {
  return post('/api/seo-admin/list', {});
};

// Downloads the current SEO taxonomy as an .xlsx workbook (see
// server/api-util/seoTaxonomyWorkbook.js) and triggers a browser save —
// a raw fetch rather than the transit-based request()/post() helpers above,
// since the response is a binary file, not transit/JSON. Rejects with the
// parsed JSON error body on a non-2xx response (e.g. 403 Not authorized),
// matching request()'s error shape so callers can handle both the same way.
export const downloadSeoAdminWorkbook = () => {
  const url = `${apiBaseUrl()}/api/seo-admin/download`;
  return window
    .fetch(url, { method: methods.POST, credentials: 'include' })
    .then(res => {
      if (res.status >= 400) {
        return res.json().then(data => {
          throw Object.assign(new Error(), data);
        });
      }
      return res.blob();
    })
    .then(blob => {
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = 'seo-taxonomy.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    });
};

// Uploads the edited .xlsx workbook — see server/api/seo-admin/upload.js.
// `file` is a browser File (e.g. from a file input's `files[0]`); read and
// base64-encoded here since this app's API router only parses Transit/JSON
// bodies (server/apiRouter.js), not multipart/form-data. Pass `confirm:
// true` to proceed past the server's "this removes a lot of existing data"
// safety check (response.needsConfirmation) once the admin has confirmed.
export const uploadSeoAdminWorkbook = (file, { confirm = false } = {}) => {
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  }).then(fileBase64 => post('/api/seo-admin/upload', { fileBase64, confirm }));
};
